import { useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import TermsModal from "../Terms/TermsModal";
import Terms from "../Terms/Terms";
import Seo from "../../components/Seo";

import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import LoginIcon from "@mui/icons-material/Login";

const initialForm = {
  email: "",
  password: "",
  password2: "",
  okToEmail: true,
};

export default function Register() {
  const navigate = useNavigate();
  const { style } = useStyle();
  const { setUser, setUpdateFlag, isLoading } = useContext(UserContext);

  const [isModalOpen, setModalOpen] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(initialForm);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleMouseDownPassword = (event) => event.preventDefault();

  const emailIsValid = useMemo(() => {
    if (!data.email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
  }, [data.email]);

  const passwordLengthValid = data.password.length >= 6;
  const passwordsMatch =
    data.password2.length === 0 ? true : data.password === data.password2;

  const canSubmit =
    data.email.trim() &&
    emailIsValid &&
    passwordLengthValid &&
    data.password2 &&
    passwordsMatch &&
    !submitting &&
    !isLoading;

  const navToLogin = () => navigate("/login");
  const navToTerms = () => navigate("/terms");

  const setField = (field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const sharedInputSx = {
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: style.color3,
        borderWidth: "1.25px",
      },
      "&:hover fieldset": {
        borderColor: style.color4,
      },
      "&.Mui-focused fieldset": {
        borderColor: style.color4,
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiInputBase-input": {
      color: style.color4,
      fontSize: "0.95rem",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: "4px",
      minHeight: "20px",
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 100px ${style.color2} inset !important`,
      WebkitTextFillColor: `${style.color4} !important`,
      caretColor: `${style.color4} !important`,
    },
  };

  const registerUser = async (e) => {
    e.preventDefault();

    const email = data.email.trim();
    const { password, password2, okToEmail } = data;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!emailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Password cannot be blank.");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (!password2) {
      toast.error("Please confirm your password.");
      return;
    }

    if (password !== password2) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);

      const registerResponse = await axios.post("/register", {
        email,
        password,
        okToEmail,
      });

      if (registerResponse.data?.error) {
        toast.error(registerResponse.data.error);
        return;
      }

      const loginResponse = await axios.post("/login", { email, password });

      if (loginResponse.data?.error) {
        toast.error(loginResponse.data.error);
        return;
      }

      const userData = loginResponse.data;

      await setUser(userData);
      localStorage.setItem("token", userData.token);
      await setUpdateFlag();

      toast.success("Registration successful!");
      window.location.href = "/";
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || "Unable to create your account right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Create Account | Parish Audio"
        description="Create your Parish Audio account."
        canonical="https://parishaudio.com/register"
        robots="noindex,follow"
      />

      <Box
        sx={{
          minHeight: "100%",
          px: 2,
          py:2,
          mt:0,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 500,
            overflow: "hidden",
            borderRadius:'10px',
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(39,55,77,0.96) 100%)`,
            border: `1px solid ${style.color2}`,
            boxShadow: "0 20px 50px rgba(0,0,0,0.32)",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.25, md: 3 },
              pt: { xs: 2.5, md: 3 },
              pb: 1.5,
              borderBottom: `1px solid rgba(255,255,255,0.08)`,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
            }}
          >
            <Stack spacing={1.25} alignItems="center" textAlign="center">

              <Typography
                variant="h5"
                sx={{
                  color: style.color4,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                }}
              >
                Create Account
              </Typography>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={registerUser}
            sx={{
              px: { xs: 2.25, md: 3 },
              py: { xs: 2.25, md: 3 },
            }}
          >
            <Stack spacing={1.25}>
              <TextField
                label="Email Address"
                type="email"
                autoComplete="email"
                fullWidth
                size="small"
                value={data.email}
                onChange={(e) => setField("email", e.target.value)}
                error={!!data.email && !emailIsValid}
                helperText={
                  !!data.email && !emailIsValid ? "Enter a valid email address." : " "
                }
                sx={sharedInputSx}
              />

              <FormControl fullWidth size="small" sx={sharedInputSx}>
                <InputLabel htmlFor="register-password">Password</InputLabel>
                <OutlinedInput
                  id="register-password"
                  type={showPassword1 ? "text" : "password"}
                  value={data.password}
                  autoComplete="new-password"
                  onChange={(e) => setField("password", e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword1((show) => !show)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: style.color3 }}
                      >
                        {showPassword1 ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
                <FormHelperText
                  sx={{
                    color:
                      !data.password
                        ? style.color3
                        : passwordLengthValid
                        ? "#66bb6a"
                        : "#ef5350",
                  }}
                >
                  {!data.password
                    ? " "
                    : passwordLengthValid
                    ? "Password looks good"
                    : "Minimum 6 characters"}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth size="small" sx={sharedInputSx}>
                <InputLabel htmlFor="register-password-confirm">
                  Confirm Password
                </InputLabel>
                <OutlinedInput
                  id="register-password-confirm"
                  type={showPassword2 ? "text" : "password"}
                  value={data.password2}
                  autoComplete="new-password"
                  onChange={(e) => setField("password2", e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowPassword2((show) => !show)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: style.color3 }}
                      >
                        {showPassword2 ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm Password"
                />
                <FormHelperText
                  sx={{
                    color:
                      !data.password2
                        ? style.color3
                        : passwordsMatch
                        ? "#66bb6a"
                        : "#ef5350",
                  }}
                >
                  {!data.password2
                    ? " "
                    : passwordsMatch
                    ? "Passwords match"
                    : "Passwords do not match"}
                </FormHelperText>
              </FormControl>

              <Box
                sx={{
                  mt: 0.25,
                  px: 1,
                  py: 0.5,
                  backgroundColor: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.okToEmail}
                      onChange={(e) => setField("okToEmail", e.target.checked)}
                      size="small"
                      sx={{
                        color: style.color3,
                        "&.Mui-checked": {
                          color: style.color4,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      Email me updates and offers
                    </Typography>
                  }
                  sx={{ my: 0 }}
                />
              </Box>

              <Button
                type="submit"
                disabled={!canSubmit}
                //sx={{ my: 2, color: style.color4, display: 'block', backgroundColor: style.color1, filter: 'brightness(1.1)', boxShadow:5,fontSize: '14pt', width: '75%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 }, }}
                sx={{
                  color: style.color3,
                  fontWeight: 700,
                  margin:'auto',
                  width:'100%',
                  backgroundColor: style.color1, 
                  color:style.color4,
                  borderWidth:'1px',
                  borderColor:style.color3,
                  filter:'brightness(1.1)',
                  borderStyle:'solid',
                  "&:hover": {

                    backgroundColor: style.color2,
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.45)",
                    background: "rgba(255,255,255,0.08)",
                    borderColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {submitting ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} sx={{ color: style.color4 }} />
                    <span>Creating account...</span>
                  </Stack>
                ) : (
                  "Create Account"
                )}
              </Button>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />

              <Stack spacing={0.75} alignItems="center">
                <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                  Already have an account?
                </Typography>

                <Button
                  onClick={navToLogin}
                  startIcon={<LoginIcon fontSize="small" />}
                  sx={{
                    px: 2.25,
                    py: 0.9,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.10)",
                    },
                  }}
                >
                  Go to Login
                </Button>
              </Stack>

              <Box sx={{ pt: 0.5, textAlign: "center" }}>
                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: "0.84rem",
                    lineHeight: 1.5,
                  }}
                >
                  By creating an account, you agree to our terms and conditions.
                </Typography>

                <Button
                  onClick={navToTerms}
                  sx={{
                    mt: 0.15,
                    minWidth: 0,
                    p: 0.25,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.84rem",
                    color: style.color4,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: style.color3,
                    },
                  }}
                >
                  View terms and conditions
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <TermsModal isOpen={isModalOpen} close={closeModal}>
        <Terms />
      </TermsModal>
    </>
  );
}

// import { useState, useEffect, useContext } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import axios from 'axios';
// import Header from '../../components/Header/Header'
// import { toast } from 'react-hot-toast';
// import ReactGA from 'react-ga4';
// import{UserContext} from '../../context/userContext/userContext';
// import { useStyle } from "../../context/styleContext/styleContext"
// import TermsModal from "../Terms/TermsModal";
// import Terms from "../Terms/Terms";
// import Button from '@mui/material/Button';
// import Visibility from '@mui/icons-material/Visibility';
// import VisibilityOff from '@mui/icons-material/VisibilityOff';
// import { Checkbox, TextField, FormControl, FormHelperText, InputLabel, OutlinedInput, InputAdornment, IconButton, FormGroup, FormControlLabel } from "@mui/material";
// import Seo from "../../components/Seo";


// export default function Register() {
//   const navigate = useNavigate();
//   const [isModalOpen, setModalOpen] = useState(false);
//   const[okToSubmit, setHideButtons] = useState(false)
//   const openModal = () => setModalOpen(true);
//   const closeModal = () => setModalOpen(false);
//   const { style, setStyle } = useStyle();
//   const [showPassword1, setShowPassword1] = useState(false);
//   const [showPassword2, setShowPassword2] = useState(false);
//   const [passwordHelper, setPasswordHelper] = useState({ message: '', color: 'red' });
//   const [confirmPasswordHelper, setConfirmPasswordHelper] = useState({ message: '', color: '' });
//   const { setUser } = useContext(UserContext);
//   const { isLoading,  } = useContext(UserContext);
//   const { setUpdateFlag } = useContext(UserContext);

//   const handleClickShowPassword1 = () => setShowPassword1((show) => !show);
//   const handleClickShowPassword2 = () => setShowPassword2((show) => !show);
//   const handleMouseDownPassword = (event) => event.preventDefault();

//   const [data, setData] = useState({
//     email: '',
//     password: '',
//     password2: '',
//     okToEmail: true,
//   });

//   const navToLogin = () => {
//     navigate('/login');
//   };

//   const handlePasswordChange = (e) => {
//     const password = e.target.value;
//     setData({ ...data, password });

//     if (password.length >= 6 && password.length > 0) {
//       setPasswordHelper({ message: 'Password OK', color: 'green' });
//     } else {
//       setPasswordHelper({ message: 'Password must be 6 characters or more', color: 'red' });
//     }
//   };

//   const checkIfOK = (e) => {

//   }

//   const handleConfirmPasswordChange = (e) => {
//     const password2 = e.target.value;
//     setData({ ...data, password2 });

//     if(data.password.length < 1)
//     {
//         setConfirmPasswordHelper({message: '', color:'red'})
//     }
//     else if(password2.length < 1)
//     {
//         setConfirmPasswordHelper({message: '', color:'red'})
//     }
//     else if (password2 !== data.password) {
//       setConfirmPasswordHelper({ message: 'Passwords do not match', color: 'red' });
//     } else {
//       setConfirmPasswordHelper({ message: 'Passwords match', color: 'green' });
//     }
//   };

//   const registerUser = async (e) => {
//     e.preventDefault();
//     var okToSend = true;

//     const { email, password, password2, okToEmail } = data;
   

//     if (!email) {
//       okToSend = false;
//       toast.error("Error: You must enter your E-Mail address to create an account");
//     }

//     if (!password) {
//       okToSend = false;
//       toast.error("Error: Password cannot be blank");
//     }

//     if (!password2) {
//       okToSend = false;
//       toast.error("Error: Please enter your password again");
//     }

//     if (password !== password2) {
//       okToSend = false;
//       toast.error("Passwords do not match");
//     }

//     if (okToSend) {
//       try {

//         console.log("sending... ", email, "  " , password)

//         const { data } = await axios.post('/register', { email, password, okToEmail });

//         if (data.error) {
//           toast.error(data.error);
//         } else {
//           setData({});

//           const{data} = await axios.post('/login', {
//             email, 
//             password,
//           });
//           const userData = data;
//           if(data.error){

//             toast.error(data.error)
//         }   else {            

//             await setUser(userData);
//             localStorage.setItem('token', data.token)
//             toast.success(data.success)
//             await setUpdateFlag()
//             //window.location.href = `/`
//     }



//           toast.success('Registration Successful. Welcome!');
//          window.location.href = `/`
//         }

//       } catch (error) {
//         console.log(error);
//       }
//     }
//   };

//   return (
//     <>
//     <Seo
//       title="Create Account | Parish Audio"
//       description="Create your Parish Audio account to manage purchases, downloads, subscriptions, and plugin access."
//       canonical="https://parishaudio.com/register"
//       robots="noindex,follow"
//     />

//       <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginTop: '25px'}}>
//         <div style={{ display: 'flex', flexDirection: 'column', width: '90%', maxWidth: '600px', margin: 'auto', color: style.color4, textAlign: 'center', marginTop: '5px', backgroundColor: style.color1, borderRadius: '10px', borderStyle: 'solid', borderColor: style.color1, borderWidth: '5px', marginBottom:'25px' }}>
//           <div style={{ width: '95%', margin: 'auto' }}>
//             <div style={{ display: 'flex', flex: 1, textAlign: 'center', marginTop: '10px' }}>
//               <h1 style={{ flex: 1, textAlign: 'center', margin: 'auto', marginBottom: '25px' }}>
//                 Create Your Account
//               </h1>
//             </div>
//             <div style={{ maxWidth: '400px', margin: 'auto' }}>
//               <div style={{ display: 'flex', flex: 1, textAlign: 'center', minWidth: '300px', margin: 'auto', marginBottom: '10px' }}>
//                 <h4 style={{ flex: 1, textAlign: 'left', margin: 'auto', color: style.color1 }}>
//                   <div style={{  }}>
//                     <TextField
//                       id="outlined-basic"
//                       label="Email"
//                       variant="outlined"
//                       value={data.email}
//                       onChange={(e) => setData({ ...data, email: e.target.value })}
//                       size='small'
//                       fullWidth
//                       sx={{
//                         backgroundColor: style.color2,
//                         borderRadius:'4px',
//                         '& .MuiInputBase-input': {
//                         color: style.color4,  // Change the font color here
//                         fontSize: '14pt',  // Change the font size here
//                         boxShadow:5,
                                                        
//                         },
//                         '& .MuiOutlinedInput-root': {
//                         '& fieldset': {
//                             borderColor: style.color1, // Change the border color if needed
//                             borderWidth:'2px'
//                         },
//                         '&:hover fieldset': {
//                             borderColor: style.color1, // Change the border color on hover if needed\
//                             borderWidth:'3px',                                                               
                     
                            
//                         },
//                         '&.Mui-focused fieldset': {
//                             borderColor: style.color1, // Change the border color when focused if needed
//                             borderWidth:'3px'
                            
//                         },
//                         '&.Mui-disabled': {
//                             '& fieldset': {
//                             borderColor: style.color1, // Change the border color when disabled
//                             },
//                         },
//                         },
//                         '& .MuiInputLabel-root': {
//                         color: style.color3, // Change the label color here
//                         fontSize:'14pt',
//                         },
//                         '& .MuiInputLabel-root.Mui-focused': {
//                         color: style.color3, // Change the label color when focused here                                 
//                         },
//                         '& .MuiInputLabel-root.Mui-disabled': {
//                         color: style.color2, // Change the label color when disabled
//                         },
//                         '& .MuiInputBase-root.Mui-disabled': {
//                         '& input': {
//                             color: style.color3, // Change the input font color when disabled // so it works in safari
//                             WebkitTextFillColor: style.color4, // so it works on chrome
//                             fontSize:'14pt'
//                         },
//                         },
//                         '& .MuiOutlinedInput-input': { color: style.color4,
//                         '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset',
//                             WebkitTextFillColor: style.color4, color:style.color4
//                          }, }, 
//                     }}
//                     />
//                   </div>
//                 </h4>
//               </div>

//               <div style={{ display: 'flex', flex: 1, flexDirection:'column', marginBottom: '10px', width: '100%', backgroundColor:style.color1}}>
//               <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   boxShadow:5,

                  

//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '& input:-webkit-autofill': {
//                     WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                     WebkitTextFillColor: `${style.color4} !important`,
//                     color: `${style.color4} !important`,
//                     caretColor: `${style.color4} !important`,
//                 },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword1 ? 'text' : 'password'}
//                     value={data.password}
//                     size='small'
//                     onChange={handlePasswordChange}
//                     sx={{
//                         '& input:-webkit-autofill': {
//                         WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                         WebkitTextFillColor: `${style.color4} !important`,
//                         color: `${style.color4} !important`,
//                         caretColor: `${style.color4} !important`,
//                     },
//                     }}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword1}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword1 ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                   />
  
//                  </FormControl>
          
//                 {/* <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '&:-webkit-autofill': {
//                       WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                       WebkitTextFillColor: `${style.color4} !important`,
//                       color: `${style.color4} !important`,
//                       caretColor: `${style.color4} !important`,
//                     },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword1 ? 'text' : 'password'}
//                     value={data.password}
//                     size='small'
//                     sx={{
//                       '& input:-webkit-autofill': {
//                       WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                       WebkitTextFillColor: `${style.color4} !important`,
//                       color: `${style.color4} !important`,
//                       caretColor: `${style.color4} !important`,
//                   },
//                   }}
//                     onChange={handlePasswordChange}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword1}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword1 ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                   />
  
//                  </FormControl> */}

//                     <FormHelperText sx={{ color: passwordHelper.color, backgroundColor:style.color1, fontSize: '12pt', width:'100%' }}>
//                         {passwordHelper.message}
//                   </FormHelperText>
//               </div>

//               <div style={{ display: 'flex', flex: 1, flexDirection:'column', marginBottom: '20px', width: '100%', backgroundColor:style.color1  }}>
//               <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   boxShadow:5,

                  

//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '& input:-webkit-autofill': {
//                     WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                     WebkitTextFillColor: `${style.color4} !important`,
//                     color: `${style.color4} !important`,
//                     caretColor: `${style.color4} !important`,
//                 },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword2 ? 'text' : 'password'}
//                     value={data.password2}
//                     size='small'
//                     onChange={handleConfirmPasswordChange}
//                     sx={{
//                         '& input:-webkit-autofill': {
//                         WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                         WebkitTextFillColor: `${style.color4} !important`,
//                         color: `${style.color4} !important`,
//                         caretColor: `${style.color4} !important`,
//                     },
//                     }}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword2}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword2 ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password Again"
//                   />
  
//                  </FormControl>
        
                
//                 <FormHelperText sx={{ color: confirmPasswordHelper.color, backgroundColor:style.color1, fontSize: '12pt' }}>
//                     {confirmPasswordHelper.message}
//                   </FormHelperText>

//                   {/* <FormGroup>
//                     <FormControlLabel checked={data.okToEmail} onChange={(e) => setData({ ...data, okToEmail: e.target.checked }) } control={<Checkbox size='large' sx={{color:style.color1, '&.Mui-checked': {
//                         color: style.color1,
//                     },}} />} label="Subscribe to Emails" />
//                 </FormGroup> */}
//               </div>


              
//                 <div style={{ marginTop: '25px', marginBottom: '30px', display: 'flex', flex: 1 }}>
//                   <Button onClick={registerUser} sx={{ my: 2, color: style.color4, display: 'block', backgroundColor: style.color1, filter: 'brightness(1.1)', boxShadow:5,fontSize: '14pt', width: '75%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 }, }}>Create Account</Button>
//                 </div>
              
//               {/* {(data.password.length < 6 || data.password != data.password2) && (
//                 <div style={{ marginTop: '50px', marginBottom: '50px', display: 'flex', flex: 1 }}>
//                   <Button disabled onClick={registerUser} sx={{ my: 2, color: style.color5, display: 'block', backgroundColor: style.color3, fontSize: '16pt', width: '100%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color4, borderWidth: '2px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color4 }, }}>Create Account</Button>
//                 </div>
//               )} */}

//               <div style={{ marginBottom: '20px' }}>
//                 <h4 style={{marginBottom:'10px'}}>Already have an account?</h4>
//                 <Button onClick={navToLogin} sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', boxShadow:5,backgroundColor: style.color1, filter: 'brightness(1.1)', fontSize: '12pt', width: '75%', minWidth: '150px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 } }}>
//                   <strong>Login</strong>
//                 </Button>
//               </div>
//               <div style={{ flex: 1, borderBottomLeftRadius: '15px', color: style.color5, marginBottom: '25px' }}>
//                 By using this website, you agree to our
//                 <div>
//                   <Button sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', backgroundColor: style.color1, fontSize: '12pt', width: '100%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color1, borderWidth: '2px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color1, color:style.color4 } }} onClick={setModalOpen.bind(null, true)}><strong>terms and conditions</strong></Button>
//                 </div>
//               </div>

//               <div id='modal-root'>
//                 <TermsModal isOpen={isModalOpen} close={closeModal}>
//                   <Terms />
//                 </TermsModal>
//               </div>
//             </div>
//           </div>
//         </div>
//         <div id='modal-root'></div>
//       </div>
//     </>
//   );
// }




